import React, {useEffect, useMemo, useRef, useState} from 'react';
import DataGrid, {Row} from 'react-data-grid';
import type {
	CellClickArgs,
	CellKeyboardEvent,
	CellKeyDownArgs,
	CellMouseEvent,
	DataGridHandle,
	FillEvent,
} from 'react-data-grid';
import {useTranslation} from 'react-i18next';
import {
	FontIconCompat as FontIcon,
	MarqueeSelectionCompat as MarqueeSelection,
	StackCompat as Stack,
	ToggleCompat as Toggle,
	useTheme,
} from 'components/FluentUICompat';
import {Selection} from 'components/FluentUICompat';
import type {ISelection} from 'components/FluentUICompat';
import {useLocation, useNavigate} from 'react-router-dom';
import {IRow, createRows} from './GridViewRows';
import {useCommand} from 'hooks';
import {UseFormGetValues, UseFormSetValue} from 'react-hook-form';
import {
	columnsGridView,
	GridViewChangedColumn,
	GridViewColumn,
	GridViewColumnType,
} from './GridViewColumns';
import {GridDialogType, GridViewDialog} from './GridViewDialog';
import {EntityListColumn, EntityListFilter} from '../EntityList';
import {GridViewInstructionsLink} from './GridViewConstants';
import './styleEntityGrid.css';

export interface DataStoreGridChangedItems {
	[key: string]: any;
}

export interface DataStoreGridChangedCells {
	[key: string]: {columns: GridViewChangedColumn[]};
}

export interface IGridView {
	savingProgress: boolean | undefined;
	itemsFiltered?: any[];
	gridViewColumns: GridViewColumn[];
	entityListColumns?: EntityListColumn[];
	dsChangedItems: DataStoreGridChangedItems;
	dsChangedCells: DataStoreGridChangedCells;
	appliedFilters?: EntityListFilter[];
	setDSChangedItems: React.Dispatch<
		React.SetStateAction<DataStoreGridChangedItems>
	>;
	setDSChangedCells: React.Dispatch<
		React.SetStateAction<DataStoreGridChangedCells>
	>;
	setValue: UseFormSetValue<any>;
	getValues: UseFormGetValues<any>;
	handleUpdateItems: (items: any[]) => Promise<void>;
	setSelectedItemsExternal?: (items: any[]) => void;
	handleLocalChange: (
		optionsCommonIntial: string[],
		selection: React.RefObject<Selection<IRow>>,
	) => (col: GridViewColumn, row: IRow) => () => Promise<void>;
	handleFill({columnKey, sourceRow, targetRow}: FillEvent<IRow>): IRow;
}

interface IGridViewHeader {
	didItemsChanged: boolean;
	rowHeight: number;
	setRowHeight: (rowHeight: number) => void;
}

interface IGridViewFooter {
	itemsFiltered?: any[];
	selection: React.RefObject<Selection<IRow>>;
}

export const tOptionsGrid = {
	ns: 'components/EntityList/Gridview',
};

const blockSize = 50;

export const GridView: React.FC<IGridView> = props => {
	const {t} = useTranslation();

	const {
		itemsFiltered,
		gridViewColumns: columns,
		entityListColumns,
		dsChangedItems,
		dsChangedCells,
		appliedFilters,
		setValue,
		setDSChangedItems,
		setDSChangedCells,
		handleUpdateItems,
		setSelectedItemsExternal,
		handleLocalChange,
		handleFill,
	} = props;

	const [optionsCommonIntial, setOptionsCommonIntial] = useState<string[]>([]);

	const [selectedRows, setSelectedRows] = useState(
		(): ReadonlySet<number> => new Set(),
	);

	const [blockNumber, setBlockNumber] = useState(0);

	const [rowHeight, setRowHeight] = useState(40);

	const [gridDialogType, setgridDialogType] = useState(GridDialogType.None);

	const refGrid = useRef<DataGridHandle>(null);
	const recoveryChecked = useRef(false);

	const [dsChangedItemsRecovered, setDSChangedItemsRecovered] =
		useState<DataStoreGridChangedItems>({});

	const [dsChangedCellsRecovered, setDSChangedCellsRecovered] =
		useState<DataStoreGridChangedCells>({});

	const theme = useTheme();
	const location = useLocation();

	const didItemsChanged = useMemo(
		() => Object.keys(dsChangedItems).length > 0,
		[dsChangedItems],
	);

	const {visibleRows, allRows} = useMemo(() => {
		const rows = createRows(itemsFiltered ?? []);
		const endRow = blockSize * (blockNumber + 1);
		return {visibleRows: rows.slice(0, endRow), allRows: rows};
	}, [itemsFiltered, dsChangedItems, blockNumber]);

	const navigate = useNavigate();

	const selection = React.useRef(
		(() => {
			const self = new Selection({
				items: allRows,
				getKey: item => item.rowId,
				//everytime the selection is changed
				onSelectionChanged() {
					const selectedGridRows = self.getSelection();
					const selectedGridRowIds = new Set(
						selectedGridRows.map(x => x.rowId),
					);
					setSelectedRows(selectedGridRowIds);
					setSelectedItemsExternal?.(selectedGridRows.map(x => x.item));
				},
			});
			return self;
		})(),
	);

	// updates the items (and their data) that can be selected on current view
	const updateSelectionItems = () => {
		selection.current.setItems(allRows);
		selection.current.setAllSelected(false);
	};

	// updates the items (and their data) that can be selected on current view and also remembers the user selection
	const updateSelectionItemsMaintainSelection = () => {
		const selectedItemsBeforeUpdate = selection.current
			.getSelection()
			.map(x => x.rowId);
		selection.current.setItems(allRows);
		selectedItemsBeforeUpdate.forEach(x =>
			selection.current.setIndexSelected(x, true, false),
		);
	};

	// renews the selection items so that external selection gets the last version of items
	// when filter changes
	useEffect(() => {
		updateSelectionItems();
	}, [appliedFilters]);

	// renews the selection items so that external selection gets the last version of items
	// when same rows but cell data has been changed
	useEffect(() => {
		updateSelectionItemsMaintainSelection();
	}, [allRows]);

	// tries to recover the changes made by user if page is accidentally closed or clicked away
	useEffect(() => {
		try {
			const recoveredCopy = localStorage.getItem(location.pathname);

			if (recoveredCopy) {
				const {dsChangedItems, dsChangedCells} = JSON.parse(recoveredCopy);

				setgridDialogType(GridDialogType.Recover);
				setDSChangedItemsRecovered(dsChangedItems);
				setDSChangedCellsRecovered(dsChangedCells);
			}
		} catch {
			localStorage.removeItem(location.pathname);
		} finally {
			recoveryChecked.current = true;
		}
	}, [location.pathname]);

	// saves the changes made by user on local storage for recovery
	useEffect(() => {
		if (!recoveryChecked.current) return;

		if (didItemsChanged) {
			localStorage.setItem(
				location.pathname,
				JSON.stringify({dsChangedItems, dsChangedCells}),
			);
		} else {
			localStorage.removeItem(location.pathname);
		}
	}, [didItemsChanged, dsChangedItems, dsChangedCells, location.pathname]);

	// users confirms data recovery
	const handleConfirmDataRecovery = () => {
		setDSChangedItems(dsChangedItemsRecovered);
		setDSChangedCells(dsChangedCellsRecovered);
	};

	// extends cell key press function with unselect all
	const handleCellKeyDown = (
		clickArg: CellKeyDownArgs<IRow>,
		e: CellKeyboardEvent,
	) => {
		if (e.key && e.key === 'Escape') {
			selection.current.setAllSelected(false);
		}
		if (e.ctrlKey && e.code === 'KeyA') {
			e.preventDefault();
			selection.current.setAllSelected(true);
		}
	};

	const arrArrayTypeColKeys = columns
		.filter(x => x.columnType === GridViewColumnType.Array)
		.map(x => x.key);

	// extends cell double click to open a form to update the data current cell selection
	const handleCellDoubleClick = (
		clickArg: CellClickArgs<IRow>,
		event: CellMouseEvent,
	) => {
		// toggles between row heigts when clicked on non editable fields
		if (clickArg.column.editable === false) {
			if (rowHeight === 40) {
				setRowHeight(240);
			} else {
				setRowHeight(40);
			}
			const cellPosition = {
				idx: clickArg.column.idx,
				rowIdx: clickArg.row.rowId,
			};
			refGrid?.current?.scrollToCell(cellPosition);
			return;
		}

		// prepares the data for the callout form of the cell based on the data type of cell column
		const colKey = clickArg.column.key;

		const selectedItems = selection.current.getSelection().map(x => x.item);

		const effectiveSelection = selectedItems.length
			? selectedItems
			: [clickArg.row.item];

		if (arrArrayTypeColKeys.includes(clickArg.column.key)) {
			const arrSelectedID = effectiveSelection.map(r => r.id);

			const arrSelectedParagraphs = (itemsFiltered ?? []).filter(
				item => arrSelectedID.indexOf(item.id) !== -1,
			);
			const matrixID: string[][] = arrSelectedParagraphs.map(p =>
				p[colKey].map((x: {id: string}) => x.id),
			);

			const arrCommonID = matrixID[0].reduce(function (res: string[], v) {
				if (
					res.indexOf(v) === -1 &&
					matrixID.every(function (a) {
						return a.indexOf(v) !== -1;
					})
				)
					res.push(v);
				return res;
			}, []);

			const dataAuto = arrSelectedParagraphs[0][colKey].filter(
				(x: {id: string}) => arrCommonID.indexOf(x.id) !== -1,
			);
			setOptionsCommonIntial(arrCommonID);
			setValue(colKey, dataAuto);
		} else {
			setValue(colKey, clickArg.row.item[colKey]);
		}
	};

	// users wants to accept the last unsaved changes
	const handleLocalChangeEffective = handleLocalChange(
		optionsCommonIntial,
		selection,
	);

	const handleFillEffective = (argsFillEvent: FillEvent<IRow>): IRow => {
		return handleFill(argsFillEvent);
	};

	// extends the  handle scroll feature to render further rows when needed
	const handleScroll = async (event: React.UIEvent<HTMLDivElement>) => {
		const boolInsideLimits =
			event.currentTarget.scrollTop + rowHeight <
			event.currentTarget.scrollHeight - event.currentTarget.clientHeight;

		if (boolInsideLimits) return;

		if ((itemsFiltered ?? []).length > blockNumber * blockSize) {
			setBlockNumber(blockNumber + 1);
		}
	};

	// makes mutations calls to server to save user progress
	const handleClickUpdateItems = async () => {
		const changedItemIds = Object.keys(dsChangedCells);
		const changedItems: any[] = changedItemIds.map(x => dsChangedItems[x]);
		await handleUpdateItems(changedItems);
		setDSChangedItems({});
		setDSChangedCells({});
	};

	const columnsGrid = columnsGridView(
		columns,
		entityListColumns ?? [],
		dsChangedCells,
		selection,
		handleLocalChangeEffective,
		theme,
	);

	useCommand(
		{
			key: 'gridViewEnd',
			text: t('gridViewEnd', tOptionsGrid),
			onClick() {
				if (didItemsChanged) {
					setgridDialogType(GridDialogType.SaveExit);
				} else {
					navigate(0);
				}
			},
			farCommand: true,
			priority: 0,
			iconProps: {
				iconName: 'GridViewSmall',
			},
		},
		[didItemsChanged],
	);

	useCommand(
		{
			key: 'cancelChanges',
			text: t('cancelChanges', tOptionsGrid),
			onClick() {
				if (didItemsChanged) {
					setgridDialogType(GridDialogType.Cancel);
				} else {
					navigate(0);
				}
			},
			farCommand: false,
			priority: 1,
			iconProps: {
				iconName: 'Cancel',
			},
		},
		[didItemsChanged],
	);

	useCommand(
		{
			key: 'metadataSave',
			text: t('metadataSave', tOptionsGrid),
			onClick() {
				setgridDialogType(GridDialogType.Save);
			},
			priority: -100,
			iconProps: {
				iconName: 'Save',
			},
		},
		[],
	);

	return (
		<>
			<Stack>
				<Stack.Item>
					<GridViewHeader {...{didItemsChanged, rowHeight, setRowHeight}} />
				</Stack.Item>
				<Stack.Item>
					<MarqueeSelection
						selection={selection.current as unknown as ISelection}
						isEnabled={true}
						onShouldStartSelection={(e: any) => e.shiftKey || e.ctrlKey}
					>
						<DataGrid
							className='rdg-light'
							headerRowHeight={40}
							rowHeight={rowHeight}
							columns={columnsGrid}
							rowKeyGetter={row => row.rowId}
							rows={visibleRows}
							selectedRows={selectedRows}
							onCellKeyDown={handleCellKeyDown}
							onCellDoubleClick={handleCellDoubleClick}
							onFill={handleFillEffective}
							onSelectedRowsChange={setSelectedRows}
							onScroll={handleScroll}
							style={{height: '70vh', userSelect: 'none'}}
							ref={refGrid}
						/>
					</MarqueeSelection>
				</Stack.Item>
				<GridViewFooter {...{itemsFiltered, selection}} />
			</Stack>
			<GridViewDialog
				gridDialogType={gridDialogType}
				setgridDialogType={setgridDialogType}
				handleConfirmDataRecovery={handleConfirmDataRecovery}
				handleClickUpdateItems={handleClickUpdateItems}
			/>
		</>
	);
};

const GridViewHeader: React.FC<IGridViewHeader> = props => {
	const {didItemsChanged, rowHeight, setRowHeight} = props;
	return (
		<Stack horizontal horizontalAlign='space-between'>
			<Stack.Item align='end'>
				<Toggle
					inlineLabel
					checked={rowHeight !== 40}
					onText='detailierte Ansicht'
					offText='Kompaktansicht'
					onChange={() => {
						if (rowHeight === 40) {
							setRowHeight(240);
						} else {
							setRowHeight(40);
						}
					}}
					style={{marginLeft: 15}}
				/>
			</Stack.Item>
			<Stack.Item align='end' style={{marginRight: 10}}>
				{didItemsChanged ? (
					<FontIcon
						iconName='TripleColumnEdit'
						style={{fontSize: 40, background: 'orange'}}
					/>
				) : (
					<FontIcon
						iconName='Cloud'
						style={{fontSize: 50, background: 'lightgreen'}}
					/>
				)}
			</Stack.Item>
		</Stack>
	);
};

const GridViewFooter: React.FC<IGridViewFooter> = props => {
	const {t} = useTranslation();
	const {itemsFiltered, selection} = props;

	const tOptionsInstruct = {keyPrefix: 'instructions', ...tOptionsGrid};
	const htmlInstructions = t('htmlInstructions', tOptionsInstruct);

	const htmlLink = `<br/><a href='${GridViewInstructionsLink}' target="_blankPage">${t(
		'furtherInfo',
		tOptionsInstruct,
	)}</a>`;

	return (
		<Stack>
			<Stack.Item align='end'>
				<div key='summary'>
					{`${t('numSegments', tOptionsGrid)}: ${
						(itemsFiltered ?? []).length
					} (${t('numSelected', tOptionsGrid)}: ${selection.current.count} )`}
				</div>
			</Stack.Item>
			<Stack.Item>
				<div
					style={{
						margin: 15,
						padding: 5,
						minWidth: '200px',
						width: '60vw',
						maxHeight: '10vh',
						maxWidth: '800px',
						border: '2px black solid',
					}}
					dangerouslySetInnerHTML={{__html: htmlInstructions + htmlLink}}
				/>
			</Stack.Item>
		</Stack>
	);
};
